/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: constants and static copy tables.
// Source: cardiac_report_v0_5_6_dev_2.html lines 2318-2331 (text constants),
// 2329-2331 (zones), 4207-4224 (REJECT_REASONS), 3197-3214 (default observed),
// 3963-3990 (feature descriptions), 3425-3427 (rhythm reference text).

export const APP_NAME = "Medaica Report"
export const APP_VERSION = "0.5.6-dev.2"

export const PATIENT_SUMMARY_TEXT =
  "This Report shares screening information from your heart-sound recording. It is intended to support " +
  "clinical review and does not replace assessment by a healthcare professional. If you feel unwell, have " +
  "symptoms, or have concerns about your heart health, seek medical advice and discuss this report with " +
  "your healthcare provider."
export const SAFE_PATIENT_SUMMARY_TEXT = PATIENT_SUMMARY_TEXT
export const SAFE_FOLLOW_UP_NOTE_TEXT =
  "This screening identified a heart-sound pattern outside the expected screening range. Please arrange " +
  "review with a healthcare professional."
export const EMERGENCY_BOILERPLATE_TEXT =
  "Seek urgent care or dial 911 if you are experiencing chest pain, fainting, severe shortness of " +
  "breath, or other urgent symptoms."
export const LEGACY_PATIENT_SUMMARY_TEXT =
  "Timing is regular during the test. Borderline structural-sound repeatability and symptom context " +
  "mean this report should be discussed with your healthcare provider."
export const LEGACY_FOLLOW_UP_NOTE_TEXT =
  "Discuss this report with your healthcare provider. Record again in a quiet setting with steady contact if asked."
export const REPORT_LIMITATION_TEXT =
  "Use with clinical evaluation; results may not be sufficient on their own as the basis for further testing."

export const UNIDIRECTIONAL_ZONES = { greenEnd: 58, amberEnd: 80 }
export const BIDIRECTIONAL_ZONES = { redLowEnd: 12, amberLowEnd: 26, greenEnd: 74, amberHighEnd: 88 }
export const CARDIAC_CYCLE_RATIO_ZONES = { redLow: 0.3, normalLow: 0.4, normalHigh: 0.67, redHigh: 0.95, maxHigh: 1.2 }

export const HANDOFF_SCHEMA = "MedaicaPcgViewerCardiacReportHandoff"

// The exam-level Structural Sound wording per status (Medaica Report v0.6.1,
// unchanged since Heart Report v0.5.8 at cardiac-panel-v4 git cf62e9d): what a
// reviewer's marker move writes and what choosing another accepted recording
// writes. The viewer's roll-up words the same three states as "No clear extra
// sound pattern identified" / "Possible repeating extra sound pattern" /
// "Clear repeating extra sound pattern"; the Report's own default value uses
// the middle one (`getDefaultObservedValue`).
export const STRUCTURAL_SOUND_STATUS_OBSERVED = {
  green: "No significant additional heart-sound energy detected",
  amber: "Additional heart-sound energy detected",
  red: "Prominent repeatable additional sound pattern",
}
/**
 * The exam-level Structural Sound sentence the roll-up writes, per status —
 * the viewer's roll-up wording (pcg-viewer-mvp 5af5c8b
 * `applyExamLevelReportRollup`). Upstream's report keeps its own three
 * sentences above for a marker move / source choice, so the two differ in
 * wording exactly as they do between the upstream viewer and report.
 */
export const STRUCTURAL_SOUND_ROLLUP_OBSERVED = {
  green: "No clear extra sound pattern identified",
  amber: "Possible repeating extra sound pattern",
  red: "Clear repeating extra sound pattern",
}

/** Format a SYS:DIA Duration value edit must satisfy (both phase durations). */
export const SYS_DIA_DURATION_EDIT_HINT =
  "Enter both phase durations, for example: SYS 320 ms · DIA 608 ms (a range per phase is accepted, e.g. SYS 278–303 ms). The SYS:DIA Ratio is edited on its separate card."
/** Format a Cycle Regularity value edit must satisfy (a timing swing). */
export const CYCLE_REGULARITY_EDIT_HINT =
  "Enter the S1-to-S1 timing variation as a percentage, for example: ±6.0%. The card grades it on its timing variability bands."
export const REVIEW_EXPORT_SCHEMA = "medaica.pcg.exam_review_export"

// The attestation a clinician confirms when approving the report (Heart Report
// v0.5.8 "Approve & Issue" statement, minus "for issue" — issuance is a
// separate step here). The server owns the wording: it refuses an approved
// save that carries anything else and records the sentence on the audit row,
// so this copy must match medaica-service's HEART_REPORT_APPROVAL_STATEMENT.
export const HEART_REPORT_APPROVAL_STATEMENT =
  "I have reviewed the timing and structural-sound information included in this report and approve the report."

export const REJECT_REASONS = [
  { code: "poor_signal_artifact", label: "Poor signal / artifact" },
  { code: "wrong_recording_position", label: "Wrong recording / position" },
  { code: "clinically_inaccurate", label: "Clinically inconsistent" },
  { code: "incomplete_analysis", label: "Incomplete analysis" },
  { code: "contradicts_history", label: "Contradicts patient history" },
  { code: "measurement_error", label: "Measurement error" },
  { code: "other", label: "Other" },
]

// The report's record of how its reviewer observations are to be read, when
// the package does not state it (Medaica Report v0.6.1 at cardiac-panel-v4
// git eba1cd1).
export const DEFAULT_RECORDING_NOTE_INSTRUCTION =
  "Viewer observations are internal HCP review context. Only a separate Report note is shown in patient and " +
  "provider reports."

// The edit-mode section of the viewer's internal observations.
export const INTERNAL_RECORDING_NOTES_INSTRUCTION =
  "HCP review and audit context only. These observations are not included in patient or provider reports."

export function rhythmRegularityReferenceText() {
  return (
    "Experimental PCG timing variability bands: Normal up to about ±8% (provider-only adjustment to " +
    "±11% when HR <60) · Moderate ±8-18% · Elevated >±18% (reference point ±25%)"
  )
}

export function getDefaultObservedValue(featureName) {
  const defaults = {
    "Signal Quality": "Fair",
    "Heart Rate": "67 bpm",
    "Timing Summary": "Regular",
    "SYS:DIA Ratio": "1 : 1.7",
    "Cardiac Cycle Timing": "1 : 1.7",
    "Systolic/Diastolic Timing": "1 : 1.7",
    "SYS:DIA Duration": "SYS 320 ms · DIA 580 ms",
    "Diastolic Duration": "SYS 320 ms · DIA 580 ms",
    "Rhythm Regularity": "Normal",
    "Cycle Regularity": "Normal",
    "S1/S2 Intensity": "1.9",
    "Structural Sound": "Possible repeating extra sound pattern",
    "Structural Sound Summary": "Possible repeating extra sound pattern",
    "Structural Sound Detail": "Sys 48% / Dia 22%",
  }
  return defaults[featureName] || null
}

export function getFeatureDescription(featureName) {
  const descriptions = {
    "Signal Quality": "Good or Fair signal is required before a report can be issued.",
    "Heart Rate": "Heartbeats per minute averaged across accepted recordings.",
    "Timing Summary": "Summary of heart rate, cardiac cycle timing, and rhythm regularity.",
    "SYS:DIA Ratio": "Systolic-to-diastolic timing relationship across accepted recordings.",
    "SYS:DIA Duration": "Mean systolic and diastolic phase durations across accepted recordings.",
    "Cardiac Cycle Timing": "Systole/diastole timing relationship between the heart's contraction and refill phases.",
    "Systolic/Diastolic Timing":
      "Systole/diastole timing relationship between the heart's contraction and refill phases.",
    "Rhythm Regularity": "How consistent the timing is from one cardiac cycle to the next.",
    "Cycle Regularity": "How consistent the timing is from one cardiac cycle to the next.",
    "S1/S2 Intensity": "Relative loudness of the first and second heart sounds.",
    "Structural Sound":
      "Additional sound patterns between or around the usual heart sounds that recur across multiple heartbeats.",
    "Structural Sound Summary":
      "Additional sound patterns between or around the usual heart sounds that recur across multiple heartbeats.",
    "Structural Sound Detail": "Where repeatable sound patterns appear within the cardiac cycle.",
    "Abnormality Score (%)": "Integrates multiple acoustic timing and intensity features",
    "Murmur Score (%)": "Based on acoustic energy patterns between S1 and S2",
    "S1:S2 Intensity Ratio": "Relative loudness of S1 vs. S2",
    "Systolic:Diastolic Duration Ratio": "S1--S2 vs. S2--S1 timing ratio (S/D)",
    "S1 Duration (ms)": "Duration of first heart sound",
    "S2 Duration (ms)": "Duration of second heart sound",
    "Systole Duration (ms)": "Duration of ventricular contraction",
    "Diastole Duration (ms)": "Duration of ventricular filling",
    "Cardiac Cycle (ms)": "Total heart cycle duration",
    "Heart Rate (BPM)": "Beats per minute",
  }
  return descriptions[featureName] || ""
}
